import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ContextualTipId, useContextualTip } from '@resistapp/client/contexts/contextual-tip-context';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { MetricMode } from '@resistapp/common/types';
import { useLayoutEffect, useRef } from 'react';
import { ContextualTip } from '../../tooltips/contextual-tips/contextual-tip';
import { ChartGroupContainer } from '../containers/chart-group-container';
import { GeneLegend } from '../legends/gene-legend';
import { HeatmapLegend } from '../legends/heatmap-legend';
import { SampleLegend } from '../legends/sample-legend';
import { MultiPlot } from './multi-plot';

const legendWidth = '210px';
export function BarBoxMap() {
  const { queryFilters } = useResearchContext();

  const { setGroupingStable, filters, toggleGeneGroupStable, setAbundanceModeStable, toggleEnvironmentStable } =
    queryFilters;

  const {
    plotData,
    setGraphAndLegendContainerWidth,
    graphAndLegendContainerWidth,
    loading,
    adminFeatures,
    project,
    tooManySamplesToShow,
  } = useResearchContext();

  const barBoxPlotRef = useRef<HTMLDivElement>(null);
  const legendBoxRef = useRef<HTMLDivElement>(null);

  const showOnlyTicks = tooManySamplesToShow;

  // Ref to anchor the contextual tip to the first legend label
  const geneLegendAnchorRef = useRef<HTMLElement>(null);
  const sampleFirstLabelRef = useRef<HTMLElement>(null);
  const sampleSecondLabelRef = useRef<HTMLElement>(null);

  const {
    visible: geneTipVisible,
    message: geneTipMessage,
    dismiss: dismissGeneTip,
    animationDelay: geneTipAnimationDelay,
    showDismiss: geneTipShowDismiss,
  } = useContextualTip(ContextualTipId.RESEARCH_GENE_LEGEND_FOCUS, MetricMode.ARGI);
  const {
    visible: sampleSelectTipVisible,
    message: sampleSelectMessage,
    dismiss: dismissSampleSelect,
    animationDelay: sampleSelectDelay,
    showDismiss: sampleSelectShowDismiss,
  } = useContextualTip(ContextualTipId.RESEARCH_SAMPLE_LEGEND_SELECT, MetricMode.ARGI);
  const {
    visible: sampleMultiTipVisible,
    message: sampleMultiMessage,
    dismiss: dismissSampleMulti,
    animationDelay: sampleMultiDelay,
    showDismiss: sampleMultiShowDismiss,
  } = useContextualTip(ContextualTipId.RESEARCH_SAMPLE_LEGEND_MULTI, MetricMode.ARGI);

  // Keep track of the available horizontal space for the plot. Using ResizeObserver
  // guarantees we react not only to window resizes but also to layout changes that
  // occur after the initial render (e.g. when the SVG plot mounts). The previous
  // implementation could lock in an erroneously small width (usually just the
  // legend width) which in turn triggered the auto-selection of a single chip in
  // ResearchContext.
  useLayoutEffect(() => {
    const plotNode = barBoxPlotRef.current;
    const legendNode = legendBoxRef.current;
    if (!plotNode || !legendNode) return;

    const computeWidth = () => {
      const newWidth = (plotNode.offsetWidth || 0) - (legendNode.offsetWidth || 0);
      // Ignore obviously wrong measurements (e.g. when content has not been laid
      // out yet) but still update later once a sensible size (>50px) is
      // available.
      if (newWidth > 50) {
        setGraphAndLegendContainerWidth(newWidth);
      }
    };

    // Initial measurement
    computeWidth();

    // Observe subsequent size changes
    const resizeObserver = new ResizeObserver(() => {
      computeWidth();
    });
    resizeObserver.observe(plotNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setGraphAndLegendContainerWidth]);

  if (!graphAndLegendContainerWidth && barBoxPlotRef.current && legendBoxRef.current) {
    const plotWidth = (barBoxPlotRef.current.offsetWidth || 2) - (legendBoxRef.current.offsetWidth || 1);
    setGraphAndLegendContainerWidth(plotWidth);
  }

  if (loading) {
    return null;
  } else if (!plotData) {
    return (
      <Flex justifyContent="center" alignItems="center" height="150px" fontSize="30px" fontWeight="bold">
        No data found for this project
      </Flex>
    );
  }

  return (
    <ChartGroupContainer style={{ paddingBottom: '36px' }} className={TestSelectors.TEST_RESEARCH_CHARTS}>
      <ChartAndLegendContainer>
        <Flex flexDirection="row" ref={barBoxPlotRef}>
          <LegendContainer ref={legendBoxRef}>
            <GeneLegend
              id={TestSelectors.DETECTED_GENES_LEGEND}
              showOptions={false}
              filters={filters}
              setGrouping={setGroupingStable}
              availableGroups={plotData.geneGroups}
              header="Detected genes"
              description="Number of genes by class"
              toggleGeneGroup={toggleGeneGroupStable}
              legendHeight="288px"
              updateSampleStatuses={adminFeatures.updateSampleStatuses}
              tipAnchorRef={geneLegendAnchorRef}
            />
            {geneTipVisible && (
              <ContextualTip
                anchorRef={geneLegendAnchorRef}
                message={geneTipMessage}
                onDismiss={dismissGeneTip}
                placement="right"
                animationDelay={geneTipAnimationDelay}
                showDismiss={geneTipShowDismiss}
              />
            )}
          </LegendContainer>
          <Box>
            <MultiPlot
              width={graphAndLegendContainerWidth}
              type="bar"
              chartId="research-view-detected-genes-chart"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
      <ChartAndLegendContainer>
        <Flex flexDirection="row">
          <LegendContainer>
            <SampleLegend
              id={TestSelectors.SAMPLES_LEGEND}
              filters={filters}
              toggleEnvironment={toggleEnvironmentStable}
              project={project}
              legendHeight="350px"
              showSampleNumbering={!showOnlyTicks}
              firstLabelRef={sampleFirstLabelRef}
              secondLabelRef={sampleSecondLabelRef}
            />
            {sampleSelectTipVisible && (
              <ContextualTip
                anchorRef={sampleFirstLabelRef}
                message={sampleSelectMessage}
                onDismiss={dismissSampleSelect}
                placement="right"
                animationDelay={sampleSelectDelay}
                showDismiss={sampleSelectShowDismiss}
              />
            )}
            {sampleMultiTipVisible && (
              <ContextualTip
                anchorRef={sampleSecondLabelRef}
                message={sampleMultiMessage}
                onDismiss={dismissSampleMulti}
                placement="right"
                animationDelay={sampleMultiDelay}
                showDismiss={sampleMultiShowDismiss}
              />
            )}
          </LegendContainer>
          <Box>
            <MultiPlot
              chartId="research-view-samples-chart"
              width={graphAndLegendContainerWidth}
              type="box"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
      <ChartAndLegendContainer>
        <Flex flexDirection="row">
          <LegendContainer>
            <HeatmapLegend
              id={TestSelectors.HEATMAP_LEGEND}
              abundanceMode={filters.abundances}
              setAbundanceMode={setAbundanceModeStable}
              legendHeight="170px"
            />
          </LegendContainer>
          <Box>
            <MultiPlot
              chartId="research-view-gene-details-chart"
              width={graphAndLegendContainerWidth}
              type="heat"
              showSampleNumbers={true}
              project={project}
              showOnlyTicks={showOnlyTicks}
            />
          </Box>
        </Flex>
      </ChartAndLegendContainer>
    </ChartGroupContainer>
  );
}

const ChartAndLegendContainer = styled(Box)`
  padding: 15px;
  padding-top: 2px;
  padding-bottom: 0;
`;

const LegendContainer = styled.div`
  min-width: ${legendWidth};
  max-width: ${legendWidth};
  height: 270px;
`;
